import { motion } from "framer-motion";
import { ArrowUpRight, Clock, Mail, MapPin, PhoneCall } from "lucide-react";
import { CycleMark } from "./Logo";
import { scrollToSection } from "./Navbar";

export default function Footer({ onQuote }) {
  return (
    <footer id="contact" data-testid="footer" className="relative overflow-hidden border-t border-white/[0.07] bg-navy-900/50">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <span className="grid h-10 w-10 place-items-center rounded-xl border border-white/10 bg-navy-800">
                <CycleMark className="h-6 w-6" />
              </span>
              <span className="font-display text-lg font-bold tracking-tight text-slate-50">
                CROWSON
              </span>
            </div>
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-slate-400">
              We offer our 22 years of experience to get your home where it needs to be.
            </p>
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
              <span className="flex items-center gap-1.5" data-testid="footer-license">
                <Clock className="h-3 w-3" /> Sun-Sat 7A-5P
              </span>
              <span className="flex items-center gap-1.5" data-testid="footer-location">
                <MapPin className="h-3 w-3" /> Nashville, TN &amp; Surrounding Areas
              </span>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-slate-500">Explore</div>
            <div className="mt-5 space-y-3">
              {[
                { id: "services", label: "Services" },
                { id: "dual", label: "Dual Climate" },
                { id: "coverage", label: "Coverage" },
                { id: "reviews", label: "Reviews" },
              ].map((l) => (
                <button
                  key={l.id}
                  data-testid={`footer-link-${l.id}`}
                  onClick={() => scrollToSection(l.id)}
                  className="group flex items-center gap-1.5 text-sm font-semibold text-slate-300 transition-colors hover:text-forge"
                >
                  {l.label}
                  <ArrowUpRight className="h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-100" />
                </button>
              ))}
            </div>
          </div>

          <div className="lg:col-span-4">
            <div
              data-testid="footer-emergency-box"
              className="rounded-2xl border border-forge/30 bg-gradient-to-br from-forge/15 to-navy-800 p-7"
            >
              <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-forge">
                <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-forge" />
                System down right now?
              </span>
              <a
                data-testid="footer-emergency-call"
                href="tel:+16155932628"
                className="mt-4 flex items-center gap-3 font-display text-2xl font-extrabold tracking-tight text-slate-50 transition-colors hover:text-forge"
              >
                <PhoneCall className="h-6 w-6 text-forge" />
                615-593-2628
              </a>
              <a
                data-testid="footer-email"
                href="mailto:crowsonhvac@yahoo.com"
                className="mt-3 flex items-center gap-3 font-display text-base font-bold tracking-tight text-slate-50 transition-colors hover:text-forge"
              >
                <Mail className="h-5 w-5 text-forge" />
                crowsonhvac@yahoo.com
              </a>
              <button
                data-testid="footer-quote-btn"
                onClick={onQuote}
                className="mt-5 w-full rounded-full bg-gradient-to-br from-forge to-forge-hi py-3.5 font-display text-xs font-bold uppercase tracking-wider text-white shadow-[0_8px_28px_rgba(255,107,0,0.35)] transition-transform duration-200 hover:scale-[1.02] active:scale-95"
              >
                Get Instant Quote
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="relative border-t border-white/[0.06]">
        <motion.div
          initial={{ y: "40%", opacity: 0 }}
          whileInView={{ y: "0%", opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          data-testid="footer-logo"
          aria-hidden="true"
          className="pointer-events-none select-none bg-gradient-to-b from-white/[0.09] to-transparent bg-clip-text text-center font-display text-[18vw] font-extrabold leading-[0.85] tracking-tight text-transparent lg:text-[15rem]"
        >
          CROWSON
        </motion.div>
        <div className="absolute inset-x-0 bottom-0 flex flex-col items-center justify-between gap-2 bg-gradient-to-t from-navy-950 via-navy-950/80 to-transparent px-6 pb-5 pt-16 text-[11px] text-slate-500 sm:flex-row">
          <span>© 2026 Crowson Home Services. All rights reserved.</span>
          <span className="font-mono uppercase tracking-[0.2em]">Ice-calibrated · Forge-grade</span>
        </div>
      </div>
    </footer>
  );
}
